import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useSkillChecklistStore = create((set) => ({
  loadingSkillCheck: false,
  totalSkillLists: 0,
  skillChecklists: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get checklist
  getSkillChecklist: async ({ query = null, successCallback }) => {
    set({ loadingSkillCheck: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_SKILL_CHECKLIST,
        null,
        query
      );
      if (response.success) {
        set({
          totalSkillLists: response.data?.pagination?.totalSkillLists || [],
          skillChecklists: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ loadingSkillCheck: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to get checklist by user
  getSkillChecklistByUserId: async ({ id, query = null, successCallback }) => {
    set({ loadingSkillCheck: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_SKILL_CHECKLIST_BY_USER,
        null,
        query,
        id
      );
      if (response.success) {
        set({
          skillChecklists: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ loadingSkillCheck: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create checklist
  createSkillChecklist: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_SKILL_CHECKLIST,
        data
      );

      if (response.success) {
        await useSkillChecklistStore.getState().getSkillChecklist({
          successCallback: () => {
            useSkillChecklistStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }

      // if (response.success) {
      //   set((state) => {
      //     const updatedSkills = [
      //       response?.data?.data,
      //       ...state.skillChecklists,
      //     ];
      //     const updatedTotalItems = updatedSkills.length;
      //     const updatedTotalPages = Math.ceil(
      //       updatedTotalItems / state.pagination.pageSize
      //     );
      //     return {
      //       skillChecklists: updatedSkills,
      //       pagination: {
      //         ...state.pagination,
      //         totalItems: updatedTotalItems,
      //         totalPages: updatedTotalPages,
      //       },
      //     };
      //   });
      //   successCallback && successCallback(response);
      // }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete checklist
  deleteSkillChecklist: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_SKILL_CHECKLIST,
        null,
        null,
        id
      );
      if (response.success) {
        set((state) => {
          const updatedSkills = state.skillChecklists.filter(
            (skill) => skill.id !== id
          );
          const updatedTotalItems = updatedSkills.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );
          return {
            skillChecklists: updatedSkills,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete soft checklist
  deleteSoftSkillChecklist: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.SOFT_DELETE_SKILL_CHECKLIST,
        null,
        null,
        id
      );
      if (response.success) {
        set((state) => {
          const updatedSkills = state.skillChecklists.filter(
            (skill) => skill.id !== id
          );
          const updatedTotalItems = updatedSkills.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );
          return {
            skillChecklists: updatedSkills,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update checklist
  updateSkillChecklist: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_SKILL_CHECKLIST,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          // Find the updated skill checklist
          const updatedSkillChecklist = state.skillChecklists.find(
            (skill) => skill.id === id
          );

          // If the skill checklist is found, update it and move it to the first position
          if (updatedSkillChecklist) {
            const updatedSkillChecklists = [
              { ...updatedSkillChecklist, ...response.data.data }, // Updated skill checklist
              ...state.skillChecklists.filter((skill) => skill.id !== id), // Rest of the skill checklists
            ];

            return {
              skillChecklists: updatedSkillChecklists,
              pagination: {
                ...state.pagination,
                currentPage: 1, // Reset currentPage to 1
              },
            };
          }

          // If the skill checklist is not found, return the state as is
          return state;
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create scaling
  createScalingChecklist: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.CREATE_SCALING_CHECKLIST,
        data
      );
      if (response.success) {
        set((state) => {
          const updatedChecklists = state.skillChecklists.map((item) => {
            if (item.id === data.skillChecklistId) {
              return {
                ...item,
                ...response.data.data,
              };
            }
            return item;
          });

          return {
            skillChecklists: updatedChecklists,
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to group in checklist
  createGroupChecklist: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_GROUP_CHECKLIST,
        data
      );
      if (response.success) {
        set((state) => {
          const updatedChecklists = state.skillChecklists.map((item) => {
            if (item.id === data.skillChecklistId) {
              return {
                ...item,
                groups: response.data.data,
              };
            }
            return item;
          });

          return {
            skillChecklists: updatedChecklists,
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useSkillChecklistStore;
